import { motion } from 'framer-motion';
import { Bot, Code2, Cpu, Layers, Puzzle, Workflow } from 'lucide-react';
import codelvesWorkshopImage from '../images/codelvesWorkshop.png';
import codelvesWorkshopViewImage from '../images/CodelvesWorkshopView.png'; 


const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: 'easeOut' },
  }),
};

const features = [
  {
    icon: Bot,
    title: 'Agentic AI',
    description: 'An AI assistant that orchestrates tasks, generates code, and manages your workspace — all through natural language.',
    span: 'md:col-span-8',
    image: codelvesWorkshopImage,
  },
  {
    icon: Cpu,
    title: 'In Browser Python',
    description: 'Python execution powered by Pyodide. No server needed — computation happens locally in your browser tab.',
    span: 'md:col-span-4',
  },
  {
    icon: Code2,
    title: 'View Page',
    description: 'A complete environment with file storage, Python execution, and seamless AI integration. All nested in the Workshop View Page.',
    span: 'md:col-span-4',
    image: codelvesWorkshopViewImage,
  },
  {
    icon: Workflow,
    title: 'Agent Orchestration',
    description: 'Built on PocketFlow patterns, agents can compose, delegate, and chain workflows. Create agents that create other agents.',
    span: 'md:col-span-8',
  },
  {
    icon: Layers,
    title: 'MCP Server Creation',
    description: 'Define and deploy Model Context Protocol servers directly from your browser. Extend your agent capabilities infinitely.',
    span: 'md:col-span-6',
  },
  {
    icon: Puzzle,
    title: 'Transformer.js',
    description: 'Run machine learning models client-side with Transformer.js. Embeddings, classification, generation — all without an API call.',
    span: 'md:col-span-6',
  },
];

export default function Features() {
  return (
    <section id="features" className="py-24 md:py-32 relative" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.5 }}
          className="mb-16"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500 mb-4">Capabilities</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-white max-w-xl" data-testid="features-title">
            Everything you need to build your own web native AI agents, in one tab.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
              variants={fadeUp}
              className={`${f.span} relative group overflow-hidden rounded-2xl border border-white/[0.08] bg-surface transition-all duration-300 hover:border-white/20`}
              data-testid={`feature-card-${i}`}
            >
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/[0.03] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <div className="relative z-10 p-8 md:p-10 flex flex-col h-full">
                <div className="w-10 h-10 rounded-lg bg-white/[0.06] border border-white/[0.08] flex items-center justify-center mb-5">
                  <f.icon className="w-5 h-5 text-neutral-300" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-medium tracking-tight text-white mb-3" data-testid={`feature-title-${i}`}>
                  {f.title}
                </h3>
                <p className="text-sm text-neutral-500 leading-relaxed flex-1">
                  {f.description}
                </p>

                {f.image && (
                  <div className="mt-6 -mx-10 -mb-10 overflow-hidden rounded-b-2xl">
                    <img
                      src={f.image}
                      alt={f.title}
                      className="w-full h-48 object-cover object-top opacity-60 group-hover:opacity-80 transition-opacity duration-500"
                    />
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
